import { useEffect, useRef, useState } from 'react';
import { logger, useQueryParams } from 'shared/lib';
import { featureFlags, MAIN_ROUTE } from 'shared/model';
import { Conversation, conversationStore } from 'entities/tavus/conversation';
import { DailyCall } from '@daily-co/daily-js';
import {
  clearSessionTime,
  getSessionTime,
  setSessionStartTime,
  updateSessionEndTime
} from 'entities/tavus/conversation/lib/sessionTIme';
import { dailyCallStore } from 'features/daily-call/model';
import {
  EPreconversationStatus,
  preconversationStore
} from 'entities/tavus/preconversation';
import { tavusPersonaStore } from 'entities/tavus/persona';
import { useNavigate } from 'react-router';

export const TIME_LIMIT = 5 * 60;

export enum EventType {
  ECHO = 'echo',
  SYSTEM_PROMPT = 'system_prompt',
  END_CALL = 'end_call'
}

interface IEvent {
  type: EventType;
  time_point: number;
  value?: string;
}

export interface ITimerConfig {
  duration: number;
  events: IEvent[];
}

const EVENTS_MAP: Partial<Record<EventType, string>> = {
  [EventType.SYSTEM_PROMPT]: 'conversation.overwrite_llm_context',
  [EventType.ECHO]: 'conversation.echo',
  [EventType.END_CALL]: 'conversation.end_call'
};

export const useTimerConversation = (
  currentConversation: Conversation | null,
  callObject: DailyCall | null
) => {
  const query = useQueryParams();
  const timerInterval = useRef<null | NodeJS.Timeout>(null);
  const [isStarted, setIsStarted] = useState(false);
  const endConversation = conversationStore((state) => state.endConversation);
  const nav = useNavigate();
  const { startLeavingCall, messages } = dailyCallStore((state) => ({
    startLeavingCall: state.startLeavingCall,
    messages: state.messages
  }));

  const { currentPreconversationId, setCurrentPreconversationId } =
    preconversationStore((state) => ({
      currentPreconversationId: state.currentPreconversationId,
      setCurrentPreconversationId: state.setCurrentPreconversationId
    }));

  const { setCurrentPersona, currentPersona } = tavusPersonaStore((state) => ({
    setCurrentPersona: state.setCurrentPersona,
    currentPersona: state.currentPersona
  }));

  const stopTimer = () => {
    setIsStarted(false);
    clearSessionTime();
    if (timerInterval.current) {
      clearInterval(timerInterval.current);
      timerInterval.current = null;
    }
  };

  const handleEndCall = () => {
    setCurrentPersona(null);
    stopTimer();
    if (currentConversation) {
      endConversation(messages).then(() => {
        const opener = window.opener;
        if (opener) {
          opener.postMessage({
            type: 'CHANGE_STATUS',
            payload: {
              status: EPreconversationStatus.FINISHED,
              messages,
              preconversationId: currentPreconversationId,
              conversationId: currentConversation.id
            }
          });
        }
        setCurrentPreconversationId(null);
      });
    }
    startLeavingCall();
    if (!window.opener) {
      nav(MAIN_ROUTE);
    }
  };

  useEffect(() => {
    const disableFeatures = query.get('disableFeatures') === 'true';
    if (!disableFeatures) {
      callObject?.on('participant-joined', (event) => {
        if (!event.participant.local) {
          logger.debug('👥 Participant joined, starting timer');
          setIsStarted(true);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (
      featureFlags.conversationTimer &&
      !timerInterval.current &&
      isStarted &&
      callObject &&
      currentConversation?.timer_config &&
      currentPersona?.timer_config_enabled
    ) {
      clearSessionTime();
      setSessionStartTime();

      logger.debug('✅ Starting timer logic');
      logger.debug('📦 Raw timer_config:', currentConversation.timer_config);

      let timerConfig: ITimerConfig = { duration: 0, events: [] };

      try {
        const raw = currentConversation.timer_config;
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;

        if (typeof parsed === 'object' && parsed.duration && parsed.events) {
          timerConfig = parsed;
        }

        logger.debug('✅ Parsed timerConfig:', timerConfig);
      } catch (e) {
        logger.error('❌ Failed to parse timer_config:', e);
      }

      if (!timerConfig) {
        logger.debug('⚠️ No valid timerConfig found — exiting timer setup');
        return;
      }

      timerInterval.current = setInterval(() => {
        const currentTime = getSessionTime();
        logger.debug('⏱️ Current session time:', currentTime);

        if (timerConfig.events && Array.isArray(timerConfig.events)) {
          timerConfig.events.forEach((action) => {
            const match = Number(currentTime) === Number(action.time_point);

            logger.debug('🔍 Checking event', {
              action,
              currentTime,
              match
            });

            if (match) {
              logger.debug('🚀 Triggered event:', action);

              if (action.type === EventType.END_CALL) {
                logger.debug('🔴 END_CALL triggered');
                handleEndCall();
              } else if (action.type === EventType.SYSTEM_PROMPT) {
                logger.debug('📡 Sending SYSTEM_PROMPT to call');
                callObject?.sendAppMessage({
                  message_type: 'conversation',
                  event_type: EVENTS_MAP[action.type],
                  conversation_id: currentConversation?.conversation_id,
                  properties: {
                    context: action.value
                  }
                });
              } else if (action.type === EventType.ECHO) {
                logger.debug('📡 Sending ECHO to call');
                callObject?.sendAppMessage({
                  message_type: 'conversation',
                  event_type: EVENTS_MAP[action.type],
                  conversation_id: currentConversation?.conversation_id,
                  properties: {
                    modality: 'text',
                    text: action.value
                  }
                });
              }
            }
          });
        }

        updateSessionEndTime();
      }, 1000);

      return () => {
        clearSessionTime();
        if (timerInterval.current) {
          clearInterval(timerInterval.current);
          timerInterval.current = null;
        }
      };
    }
  }, [isStarted, currentConversation]);

  return stopTimer;
};
